import { FunctionComponent } from "react";
import { styled, useTheme } from "@mui/material";
import {
  StandardHeader,
  Props as BaseTileHeaderProps,
  tileLogoMargin,
  useFilterTileNames,
} from "./StandardTileHeader";
import {
  StackedDealName,
  StackedSavingAmount,
  StackedPartnerName,
} from "./StackedTileHeader";
import EarningsBox from "../EarningsBox";
import TileLogo from "./TileLogo";
import { getTileSpacing } from ".";

const CompactHeader = styled(StandardHeader)(({ theme }) => ({
  flexWrap: "nowrap",
  marginBottom: getTileSpacing(theme),
  paddingTop: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingLeft: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
  paddingRight: theme.settings.tiles.padding
    ? theme.typography.pxToRem(theme.settings.tiles.padding)
    : "0.8rem",
}));

const TileLogoContainer = styled("div")(() => ({
  marginRight: tileLogoMargin,
}));

const logoSize = 50;
const RightSection = styled("div")(() => ({
  maxWidth: `calc(100% - ${logoSize}px - ${tileLogoMargin})`,
}));

const PartnerName = styled(StackedPartnerName)(() => ({
  margin: 0,
}));

const DealName = styled(StackedDealName)(() => ({
  margin: 0,
}));

const CompactTileHeader: FunctionComponent<BaseTileHeaderProps> = ({
  id,
  item: { name, seller, deal },
}: BaseTileHeaderProps) => {
  const theme = useTheme();
  const logo = seller.symbolLogoUrl || seller.logoUrl;
  const {
    showVendorName,
    name: tileNameConfig,
    showSavingAmount,
  } = theme.settings.tiles.titleContent;
  const { dealName, listingName } = useFilterTileNames(
    tileNameConfig,
    name,
    deal?.title,
  );

  return (
    <CompactHeader id={id}>
      {logo && (
        <TileLogoContainer>
          <TileLogo
            logoUrl={logo}
            orgName={seller.name}
            width={50}
            height={50}
          />
        </TileLogoContainer>
      )}
      <RightSection>
        {showVendorName && (
          <PartnerName variant="h3" noWrap title={seller.name}>
            {seller.name}
          </PartnerName>
        )}
        {listingName && (
          <DealName variant={showVendorName ? "h4" : "h3"} title={listingName}>
            {listingName}
          </DealName>
        )}
        {dealName && (
          <DealName variant={showVendorName ? "h4" : "h3"} title={dealName}>
            {dealName}
          </DealName>
        )}

        {showSavingAmount && !!deal?.averageTotalSavingsAmount && (
          <StackedSavingAmount>
            <EarningsBox
              color={theme.palette.tileOthersColor.main}
              averageTotalSavingsAmount={deal.averageTotalSavingsAmount}
              averageTotalSavingsPrefix={deal.averageTotalSavingsPrefix}
              averageTotalSavingsCurrency={deal.averageTotalSavingsCurrency}
            />
          </StackedSavingAmount>
        )}
      </RightSection>
    </CompactHeader>
  );
};

export default CompactTileHeader;
